import { ContentSchedule } from "entities/contentSchedule/types";
import { MouseEvent, useMemo, useState } from "react";
import { FilterOption, FilterProps } from "shared/components/Filter";
import { useAnchorEl } from "shared/hooks/useAnchorEl";

type FilterKey = "brand" | "page";

type FilterConfig = {
  key: FilterKey;
  label: string;
  sortAlphabetically?: boolean;
};

export type ContentScheduleFilterHook = {
  filtersData: FilterProps[];
  filteredModules: ContentSchedule[];
  searchValue: string;
  setSearchValue: (value: string) => void;
};

const BRAND_LABEL_MAP: Record<string, string> = {
  free: "Free to Play",
  international: "International",
  telegram: "Telegram",
};

const filterConfigs: FilterConfig[] = [
  {
    key: "brand",
    label: "Brand",
    sortAlphabetically: true,
  },
  {
    key: "page",
    label: "Page",
    sortAlphabetically: true,
  },
];

export const useContentScheduleFilter = (
  modules: ContentSchedule[],
): ContentScheduleFilterHook => {
  const [selectedValues, setSelectedValues] = useState<{
    [key in FilterKey]?: FilterOption[];
  }>({});
  const [openStates, setOpenStates] = useState<{
    [key in FilterKey]?: boolean;
  }>({});
  const { anchorEl, handleAnchorClose, handleAnchorOpen } = useAnchorEl();
  const [searchValue, setSearchValue] = useState<string>("");

  const filterOptions = useMemo(() => {
    const brandMap = new Map<string, string>();
    modules.forEach((module) => {
      if (module.brandId) {
        brandMap.set(module.brandId, module.brandId);
      }
    });

    const brandOptions = Array.from(brandMap.entries())
      .map(([value, label]) => ({
        value,
        label: BRAND_LABEL_MAP[value] || label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const selectedBrands = selectedValues.brand || [];
    const filteredModules =
      selectedBrands.length > 0
        ? modules.filter((module) =>
            selectedBrands.some((brand) => brand.value === module.brandId),
          )
        : modules;

    const pageNameMap = new Map<string, string>();
    filteredModules.forEach((module) => {
      if (module.pageName) {
        pageNameMap.set(module.pageId, module.pageName);
      } else if (module.pageId) {
        pageNameMap.set(module.pageId, module.pageId);
      }
    });

    const pageOptions = Array.from(pageNameMap.entries())
      .map(([pageId, pageName]) => ({
        value: pageId,
        label: pageName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      brand: brandOptions,
      page: pageOptions,
    };
  }, [modules, selectedValues.brand]);

  const filtersData: FilterProps[] = filterConfigs
    // Only include page filter if at least one brand is selected
    .filter((config) => {
      if (config.key === "page") {
        const selectedBrands = selectedValues.brand || [];
        return selectedBrands.length > 0;
      }
      return true;
    })
    .map(({ key, label, sortAlphabetically }) => {
      const options = filterOptions[key] || [];

      const handleChangeValue = (
        _: MouseEvent<HTMLElement>,
        option: FilterOption,
      ) => {
        setSelectedValues((prev) => {
          const currentOptions = prev[key] || [];
          const isOptionSelected = currentOptions.some(
            (o) => o.value === option.value,
          );

          const newValues = isOptionSelected
            ? currentOptions.filter((o) => o.value !== option.value)
            : [...currentOptions, option];

          return {
            ...prev,
            [key]: newValues,
          };
        });
      };

      const onClearSelectedValues = () =>
        setSelectedValues((prev) => ({ ...prev, [key]: [] }));

      const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setOpenStates((prev) => ({ ...prev, [key]: true }));
        handleAnchorOpen(event);
      };

      const handleClose = () => {
        setOpenStates((prev) => ({ ...prev, [key]: false }));
        handleAnchorClose();
      };

      return {
        title: label,
        options,
        anchorEl,
        open: openStates[key] || false,
        selectedValue: selectedValues[key] || [],
        handleOpenMenu,
        handleChangeValue,
        handleClose,
        onClearSelectedValues,
        disabled: false,
        sortAlphabetically,
      };
    });

  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const selectedBrands = selectedValues.brand || [];
      if (
        selectedBrands.length > 0 &&
        !selectedBrands.some((brand) => brand.value === module.brandId)
      ) {
        return false;
      }

      const selectedPages = selectedValues.page || [];
      if (
        selectedPages.length > 0 &&
        !selectedPages.some((page) => page.value === module.pageId)
      ) {
        return false;
      }

      if (
        searchValue &&
        !module.name?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [modules, selectedValues, searchValue]);

  return {
    filtersData,
    filteredModules,
    searchValue,
    setSearchValue,
  };
};
